import { useEffect, useState } from 'react';
import { collection, doc, onSnapshot, orderBy, query, updateDoc, writeBatch, limit as fsLimit, serverTimestamp } from 'firebase/firestore';
import { getAuth, onAuthStateChanged } from 'firebase/auth';
import { db } from '../firebase';

// Campana del panel — igual patrón que puro_clean: un doc por destinatario en
// notifications/{uid}/items, escrito por el backend (nunca por el navegador,
// las reglas de Firestore solo dejan tocar read/attendedAt desde aquí).
export function useNotifications() {
  const [uid, setUid] = useState(() => getAuth().currentUser?.uid || null);
  const [notifications, setNotifications] = useState([]);

  useEffect(() => {
    const unsub = onAuthStateChanged(getAuth(), (user) => setUid(user?.uid || null));
    return unsub;
  }, []);

  useEffect(() => {
    if (!uid) { setNotifications([]); return undefined; }
    const q = query(collection(db, 'notifications', uid, 'items'), orderBy('createdAt', 'desc'), fsLimit(50));
    const unsub = onSnapshot(q, (snap) => {
      setNotifications(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
    }, (err) => console.error('[useNotifications]', err.message));
    return unsub;
  }, [uid]);

  // "Leída" manda sobre todo lo demás: una vez `read` es true (ya sea porque
  // se le dio clic o porque se cerró la campana), deja de contar para el
  // badge y de aparecer como pendiente — aunque sea `requiresAction`. Antes
  // `requiresAction` solo se apagaba con `attendedAt` (entrar al lead
  // específico), así que cerrar la campana nunca las quitaba y volvían a
  // salir en cada refresh.
  const actionRequired = notifications.filter((n) => n.requiresAction && !n.read && !n.attendedAt);
  const unreadCount = notifications.filter((n) => !n.read).length;
  const displayCount = actionRequired.length || unreadCount;

  const markAttended = async (id) => {
    if (!uid) return;
    try {
      await updateDoc(doc(db, 'notifications', uid, 'items', id), { read: true, attendedAt: serverTimestamp() });
    } catch (err) {
      console.error('[useNotifications] markAttended', err.message);
    }
  };

  // Para cuando se cierra UN toast puntual con "×" — distinto de markAttended
  // (que además implica "ya lo resolví y navegué al lead"). Esto solo dice
  // "ya lo vi", que es justo lo que hace el botón de cerrar.
  const markRead = async (id) => {
    if (!uid) return;
    try {
      await updateDoc(doc(db, 'notifications', uid, 'items', id), { read: true });
    } catch (err) {
      console.error('[useNotifications] markRead', err.message);
    }
  };

  const markAllRead = async () => {
    if (!uid) return;
    const unread = notifications.filter((n) => !n.read);
    if (!unread.length) return;
    try {
      const batch = writeBatch(db);
      unread.forEach((n) => batch.update(doc(db, 'notifications', uid, 'items', n.id), { read: true }));
      await batch.commit();
    } catch (err) {
      console.error('[useNotifications] markAllRead', err.message);
    }
  };

  return { notifications, actionRequired, unreadCount, displayCount, markAttended, markAllRead, markRead };
}

export default useNotifications;
